import BigNumber from 'bignumber.js'
import { getToken } from '../model/tokens/helper'
// import { getPoolById, getPoolIdFromTokensNew } from '../model/pools/helper'
import { addToastMsg } from '../services/amm'
import { Token } from '../atoms'

export function getTokenUSDPrice(tokenAmount: number, usdPrice: number) {
    const price = tokenAmount * usdPrice
    return price.toFixed(2)
}

export function calculateAPR(volume7Day: number, poolFee: number, tvl: number) {
    return (((volume7Day * poolFee) / 100 / tvl + 1) ** 52.14 * 100 - 100).toFixed(2)
}

export function calculateAPR24(volume24: number, poolFee: number, tvl: number) {
    return (((volume24 * poolFee) / 100 / tvl) * 365 * 100).toFixed(2)
}
export const numFormat = (num: string, isPercent = false): string => {
    if (num === undefined || num === '') return ''

    if (isPercent) {
        let res = new BigNumber(num).shiftedBy(2).sd(4, BigNumber.ROUND_DOWN).toPrecision(4)
        if (new BigNumber(res).eq(0)) return '0'
        if (new BigNumber(res).lt(0.01)) return '<0.01'
        if (new BigNumber(res).gt(1000)) {
            return BigNumber(res).toFixed(0)
        }
        return res
    }

    if (new BigNumber(num).gt(10 ** 10)) return new BigNumber(num).toFixed(0)

    // BigNumber(0.012340000).sd(2).toString(10) --> '0.012'
    let res = new BigNumber(num).sd(6, BigNumber.ROUND_DOWN).toFixed(18, BigNumber.ROUND_DOWN) // FIXME: need to align to token decimals (not 18).... 6 is a magic number...
    // patch:
    return new BigNumber(res).toString(10)
}

function roundTo4DecimalPlaces(num: BigNumber, prec = 4): BigNumber {
    const precision = new BigNumber(`1e+${prec}`)
    return num.times(precision).integerValue(BigNumber.ROUND_HALF_UP).dividedBy(precision)
}

export function formatFloatingPointNumber(input: number | BigNumber | string, max_decimals = 4): string {
    const bigNumber = new BigNumber(input)

    // Construct the threshold string dynamically based on max_dec
    const thresholdString = `0.${'0'.repeat(max_decimals - 1)}1`
    const threshold = new BigNumber(thresholdString)

    if (!bigNumber.isZero()) {
        // Check if the number is smaller than the threshold
        if (bigNumber.isLessThan(threshold)) {
            return `<${thresholdString}`
        }

        const insigfinicatDigits = countInsignificantDigits(bigNumber.toNumber())
        const roundedNumber = roundTo4DecimalPlaces(bigNumber, insigfinicatDigits > 4 ? insigfinicatDigits - 2 : 4)
        const roundedString = roundedNumber.toString()

        const [integerPart, decimalPart] = roundedString.split('.')

        const formattedInteger = new Intl.NumberFormat().format(Number(integerPart))

        const formattedNumber = decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger

        return formattedNumber
    }

    return '0'
}

function countInsignificantDigits(num: number): number {
    if (num < 1) {
        const numStr = num.toString()

        for (let i = 0; i < numStr.length; i++) {
            if (numStr[i] !== '0' && numStr[i] !== '.') {
                return i + (numStr[i] === '.' ? 0 : 1)
            }
        }
    }
    return 0
}

export const numFormatTruncated = (num: string, max_decimals = 4): string => {
    if (num === undefined || num === '') return ''

    const number = new BigNumber(num)
    if (number.eq(0)) {
        return '0'
    }
    // Construct the threshold string dynamically based on max_dec
    const thresholdString = `0.${'0'.repeat(max_decimals - 1)}1`
    const threshold = new BigNumber(thresholdString)

    // Check if the number is smaller than the threshold
    if (number.isLessThan(threshold)) {
        return `<${thresholdString}`
    }

    const factor = new BigNumber(`1e${max_decimals}`)
    const truncated = number.multipliedBy(factor).integerValue(BigNumber.ROUND_DOWN).dividedBy(factor)
    return truncated.toString()
}

export function decimal_int_to_float(network: string, token_address: string, amount: string): string {
    const token = getToken(network, token_address)
    if (token === undefined) {
        return '0'
    }
    return new BigNumber(amount).shiftedBy(-token.decimals).toString(10) // should have exactly 'decimals' floating point
}

export function float_to_decimal_int(network: string, token_address: string, amount: string): string {
    const token = getToken(network, token_address)
    if (token === undefined) {
        addToastMsg('float_to_decimal_int: Undefined pool for token ' + token_address + ' amount=' + amount, 'warning')
        return '0'
    }

    return new BigNumber(amount).shiftedBy(token.decimals).toFixed(0, BigNumber.ROUND_DOWN)
}

export function findClosestTick(tickNumber: number, tickSpacing: number): number {
    if (tickNumber < 0) return Math.ceil(tickNumber / tickSpacing) * tickSpacing
    return Math.floor(tickNumber / tickSpacing) * tickSpacing
}
const MIN_RANGE_TICK = -887270
const MAX_RANGE_TICK = 887270

export function getLowestTick(tickSpacing: number): number {
    const lowestTick = MIN_RANGE_TICK / tickSpacing / 2
    return findClosestTick(lowestTick, tickSpacing)
}

export function calculateLowestAndHighestTicks(ticksInRange: number, tickSpacing: number) {
    const centerTick = Math.floor(ticksInRange / 2)
    const isEvenTicks = ticksInRange % 2 === 0

    let lowestTick
    let highestTick

    if (isEvenTicks) {
        lowestTick = centerTick - Math.floor(ticksInRange / 2) - 1
        highestTick = centerTick + Math.floor(ticksInRange / 2)
    } else {
        lowestTick = centerTick - Math.floor(ticksInRange / 2)
        highestTick = centerTick + Math.floor(ticksInRange / 2)
    }

    const lowestTickValue = lowestTick * tickSpacing
    const highestTickValue = highestTick * tickSpacing

    return { lowestTick, highestTick, lowestTickValue, highestTickValue }
}

// export function calculateLowestAndHighestTicks(ticksInRange: number, tickSpacing: number) {
//   const centerTick = Math.floor(ticksInRange / 2);
//   const lowestTick = centerTick - Math.floor(ticksInRange / 2);
//   const highestTick = centerTick + Math.floor(ticksInRange / 2);

//   const lowestTickValue = lowestTick * tickSpacing;
//   const highestTickValue = highestTick * tickSpacing;

//   return { lowestTick, highestTick, lowestTickValue, highestTickValue };
// }
// export function calculateLowestAndHighestTicks(ticksInRange: number, tickSpacing: number) {
//     const centerTick = Math.floor(ticksInRange / 2);
//     const lowestTick = centerTick - (ticksInRange % 2 === 0 ? 1 : 0);
//     const highestTick = centerTick + (ticksInRange % 2 === 0 ? 0 : 1);

//     const lowestTickValue = lowestTick * tickSpacing;
//     const highestTickValue = highestTick * tickSpacing;

//     return { lowestTick, highestTick, lowestTickValue, highestTickValue };
// }

export function getHighestTick(tickSpacing: number): number {
    const highestTick = MAX_RANGE_TICK / tickSpacing / 2
    return findClosestTick(highestTick, tickSpacing)
}

export function calculatePriceBounds(lowerTick: number, upperTick: number, tickSpacing: number) {
    const lowerPriceBound = lowerTick * tickSpacing
    const upperPriceBound = upperTick * tickSpacing

    return { lowerPriceBound, upperPriceBound }
}

export function getAmountTokenBForTokenA(tokenAIn: number, tickSpacing: number) {
    const tokenBIn = Math.round(tokenAIn / (tickSpacing * tickSpacing)) // Round up to the nearest integer

    return tokenBIn
}

export function calculateTokenPrice(tokenPrice: number, tickSpacing: number): number {
    const tick = Math.round(Math.log(tokenPrice) / Math.log(1.0001) / tickSpacing) * tickSpacing
    const tokenPriceAtTick = Math.pow(1.0001, tick)
    return tokenPriceAtTick
}

export function calculateMinMaxPriceETHtoUSDC(lowerTick: number, upperTick: number, tickSpacing: number) {
    const lowerPrice = (1 / Math.pow(1.0001, upperTick)) * tickSpacing
    const upperPrice = (1 / Math.pow(1.0001, lowerTick)) * tickSpacing

    return { minPriceETHtoUSDC: lowerPrice, maxPriceETHtoUSDC: upperPrice }
}

export function calculateTickFromPrice(price: number, tickSpacing: number) {
    const tick = Math.round(Math.log(price) / Math.log(1.0001) / tickSpacing)
    let r = tick * tickSpacing
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (r < -887272) {
            r += tickSpacing
        } else if (r > 887272) {
            r += tickSpacing
        } else {
            return r
        }
    }
}
// export function calculateTickFromPrice(price, tickSpacing) {
//     const tick = Math.floor(Math.log(price) / Math.log(1.0001) / tickSpacing);
//     return tick;
//   }

export function calculatePriceFromTick(tick: number) {
    return 1.0001 ** tick
}

export function calculateRatioFromTick(tick: number) {
    return Math.pow(1.0001, tick)
}

export function calculateRatioFromTickForTokens(tick: number, token1: Token, token2: Token): BigNumber {
    const r = calculateRatioFromTick(tick)
    return BigNumber(r).multipliedBy(10 ** (token1.decimals - token2.decimals))
}

export function normalizeForTokens(value: BigNumber, token1: Token, token2: Token): BigNumber {
    // console.log('YSYS norm token1=', token1, ' token2=', token2, ' value=', value.toString())
    return value.multipliedBy(BigNumber(10).pow(token1.decimals - token2.decimals))
}

export function normalizeForToken(value: BigNumber, token: Token): BigNumber {
    // console.log('YSYS norm token1=', token1, ' token2=', token2, ' value=', value.toString())
    return value.dividedBy(BigNumber(10).pow(token.decimals))
}

export function getFeeMultiplierDivider(fee: number) {
    let maxMultiplier = 3
    let minDivider = 3
    if (fee === 0.3) {
        maxMultiplier = 1.5
        minDivider = 1.5
    }
    if (fee === 0.05) {
        maxMultiplier = 1.2
        minDivider = 1.2
    }
    if (fee === 0.01) {
        maxMultiplier = 1.04
        minDivider = 1.04
    }
    return { maxMultiplier, minDivider }
}

export function formatUSD(amount: number): string {
    if (amount >= 1000000000) {
        return (amount / 1000000).toFixed(2) + 'B'
    } else if (amount >= 1000000) {
        return (amount / 1000000).toFixed(2) + 'M'
    } else if (amount >= 1000) {
        return (amount / 1000).toFixed(2) + 'K'
    } else {
        return amount.toFixed(2)
    }
}

// remove sqrt from name
export function calc_sqrt_price(token0_in_terms_of_token1: BigNumber, decimals0: number, decimals1: number): BigNumber {
    // Adjust for token decimals
    const priceRatio = token0_in_terms_of_token1.multipliedBy(new BigNumber(10).pow(decimals1 - decimals0))
    // Calculate the square root of the price ratio
    const sqrtPrice = new BigNumber(priceRatio)
    // Scale it up to the 96 bits fixed-point format
    return sqrtPrice
    // return sqrtPrice.multipliedBy(new BigNumber(2).pow(96))
}

export function formatNumber(number: number | string): string {
    const parts = number.toString().split('.')
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const decimalPart = parts[1] ? `.${parts[1].slice(0, 2)}` : ''
    return integerPart + decimalPart
}

export function calcTickRangesFromPercent(currentRatio: number, tickAmount: number, percent: number): { min_tick: number; max_tick: number } {
    const high_price = BigNumber(currentRatio).multipliedBy((100 + percent) / 100)
    const low_price = BigNumber(currentRatio).pow(2).dividedBy(high_price)
    return {
        min_tick: calculateTickFromPrice(low_price.toNumber(), tickAmount),
        max_tick: calculateTickFromPrice(high_price.toNumber(), tickAmount)
    }
}
